import type { ErrorCode, Locale, SubmitPresenceResponse } from '@sentra/contracts';
import { isErrorCode, toErrorBody } from '@sentra/contracts';
import {
  BadgeCheck,
  Ban,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  CircleSlash,
  Clock,
  CloudOff,
  KeyRound,
  ShieldAlert,
  TriangleAlert,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { isApiError, TransportError, UNFILLED_PLACEHOLDER } from '@/api/errors';
import { cn } from '@/design/utils';

/**
 * Section 1.11's outcome matrix, as a closed union with one treatment per row.
 *
 * **Every row is here because undefined behaviour at a gate becomes improvised behaviour.** The
 * operator has somebody standing in front of them; "something went wrong" is not an instruction,
 * and two different situations that look the same on screen are two situations an operator will
 * handle the same way. So each row has its own tone, its own icon and its own sentence, and the
 * union is exhaustive rather than open: a code this module has not been taught about cannot be
 * rendered as one of the others by accident.
 *
 * **Three sources feed it and they are not interchangeable.** A resolve that refuses answers an
 * AD-35 error body, which is a scan that never reached a person. A submission that refuses answers
 * 200 with a `refusal`, because the movement was decided and WAS recorded — the event exists and
 * the trail knows it. And the transport can fail, which is neither: nothing was decided and the
 * submission is safe to retry, because AD-30's key makes a retry a replay.
 *
 * **The tone is not decoration.** `accepted` is the only one that says "let them through";
 * `anomaly` is the full-screen red alert with the sound; `stop` is a refusal the operator cannot
 * override at this terminal; `warning` is a pass problem with a next step (reissue, registration);
 * and `offline` is the one state where nothing is known. Colour is never the only carrier: each
 * treatment has an icon and a sentence, because Section 1.12's rule about charts is a rule about
 * this product.
 */

/** What the operator must do next, which is what decides the treatment. */
type GateTone = 'accepted' | 'anomaly' | 'stop' | 'warning' | 'offline';

export type GateOutcome =
  /** The direction matched the recorded state. */
  | { readonly kind: 'accepted' }
  /** A contradicting direction a human overrode with a reason. */
  | { readonly kind: 'override' }
  /** A blocked person an operator let through, with a reason on the record. */
  | { readonly kind: 'blocked_override' }
  /** Blocked, and no reason has been given yet. The refusal WAS recorded (AD-31). */
  | { readonly kind: 'blocked' }
  /** The record is archived: the answer is reactivation, not an override. */
  | { readonly kind: 'archived' }
  /** An override this caller may not make. A browser operator cannot reach it; a machine can. */
  | { readonly kind: 'override_not_permitted' }
  /** A contradicting direction with no reason chosen yet. Nothing was written. */
  | { readonly kind: 'reason_required' }
  /** The same idempotency key again: the original event, with the time it happened. */
  | { readonly kind: 'duplicate'; readonly occurredAt: string }
  | { readonly kind: 'revoked' }
  | { readonly kind: 'superseded' }
  /** A band from an imported batch that nobody has registered yet (AD-60). */
  | { readonly kind: 'blank_unclaimed' }
  /** A band from a withdrawn batch, or one withdrawn on its own. */
  | { readonly kind: 'blank_void' }
  | { readonly kind: 'unknown' }
  | { readonly kind: 'malformed' }
  | { readonly kind: 'rate_limited'; readonly retryAfterSeconds: number | null }
  /** The stored index was built under a different normalizer version. Nothing can resolve it. */
  | { readonly kind: 'index_version_mismatch' }
  /**
   * Nothing was decided, and a retry is safe (AD-30).
   *
   * Two different facts arrive here and Section 1.11's last row covers both: the request never
   * reached the server, and the server could not reach its own database. From the operator's side
   * they are one situation — the connection is lost, nothing has been recorded, and the same scan
   * goes out again under the same key — so they are one row rather than two.
   */
  | { readonly kind: 'offline' }
  /**
   * A refusal this screen has no specific treatment for.
   *
   * It carries the code AND the sentence the server rendered for it, because "rendered from its own
   * code" was a claim the banner did not honour: the code sat in this row unread and every refusal
   * outside the twelve below reached a door as "no se ha podido registrar", which tells the operator
   * holding the queue nothing at all. `message` is the fallback for the codes whose own sentence
   * interpolates a value only the server had — a role, a scope, a direction, a number of minutes.
   */
  | { readonly kind: 'refused'; readonly code: ErrorCode; readonly message: string | null };

/** The error codes a resolve or a submission answers, mapped to the row each one is. */
const REFUSAL_OUTCOMES: Readonly<Record<string, GateOutcome['kind']>> = {
  CREDENTIAL_REVOKED: 'revoked',
  CREDENTIAL_SUPERSEDED: 'superseded',
  CREDENTIAL_BLANK_UNCLAIMED: 'blank_unclaimed',
  CREDENTIAL_BLANK_VOID: 'blank_void',
  CREDENTIAL_CODE_UNKNOWN: 'unknown',
  CREDENTIAL_CODE_MALFORMED: 'malformed',
  CREDENTIAL_INDEX_VERSION_MISMATCH: 'index_version_mismatch',
  PERSON_ARCHIVED: 'archived',
  PERSON_BLOCKED: 'blocked',
  PRESENCE_OVERRIDE_NOT_PERMITTED: 'override_not_permitted',
  PRESENCE_REASON_REQUIRED: 'reason_required',
  // **Section 1.11's last row, from the server's side of it.** `SYSTEM_DEPENDENCY_UNAVAILABLE` is
  // what the api answers when it could not reach its own database — the one masked fault it
  // distinguishes, because the operator's next move differs: this is "connection lost, retrying"
  // with the attempt kept and the retry offered, where every other refusal is a decision with no
  // retry. Without this row the outage rendered as `refused` with an unexplained sentence and no
  // way forward, on the screen of somebody standing at a door with a queue behind them.
  SYSTEM_DEPENDENCY_UNAVAILABLE: 'offline',
};

/**
 * The row a thrown failure is.
 *
 * A transport failure is `offline` rather than a refusal, which is the distinction AD-44 turns on:
 * the gate degrades rather than stops, the operator is told to retry, and the retry is safe because
 * the key was minted before the first attempt. A 503 saying the installation could not reach its
 * own database is the same row, reached from the other side of a wire that did work; see the
 * `SYSTEM_DEPENDENCY_UNAVAILABLE` entry above.
 */
export function outcomeOfFailure(error: unknown): GateOutcome {
  if (error instanceof TransportError) {
    return { kind: 'offline' };
  }
  if (!isApiError(error)) {
    return { kind: 'refused', code: 'SYSTEM_INTERNAL_ERROR', message: null };
  }
  if (error.code === 'SYSTEM_RATE_LIMITED') {
    return { kind: 'rate_limited', retryAfterSeconds: error.retryAfterSeconds };
  }
  const mapped = REFUSAL_OUTCOMES[error.code];
  return mapped === undefined
    ? { kind: 'refused', code: error.code, message: error.serverMessage }
    : ({ kind: mapped } as GateOutcome);
}

/**
 * The row a RECORDED submission is.
 *
 * A replay is checked first and is the reason this cannot be derived from `result` alone: a
 * replayed answer carries the ORIGINAL event, whose result may be `accepted`, and showing a second
 * green confirmation for one movement is exactly what Section 1.11 forbids.
 */
export function outcomeOfSubmission(answer: SubmitPresenceResponse): GateOutcome {
  if (answer.replayed) {
    return { kind: 'duplicate', occurredAt: answer.event.occurredAt };
  }
  if (answer.refusal !== null) {
    const mapped = REFUSAL_OUTCOMES[answer.refusal.code];
    if (mapped !== undefined) {
      return { kind: mapped } as GateOutcome;
    }
    // A recorded refusal travels as a code and nothing else (`recordedRefusalSchema`), so there is
    // no server sentence to fall back on: what this side can render, it renders, and the rest is
    // the row's own body.
    const code = answer.refusal.code;
    return {
      kind: 'refused',
      code: isErrorCode(code) ? code : 'SYSTEM_INTERNAL_ERROR',
      message: null,
    };
  }
  switch (answer.event.result) {
    case 'accepted': {
      return { kind: 'accepted' };
    }
    case 'override': {
      return { kind: 'override' };
    }
    case 'blocked_override': {
      return { kind: 'blocked_override' };
    }
    case 'rejected': {
      // A rejection with no refusal is a shape the domain does not produce; rendering it as a
      // success would be the one mistake this module exists to prevent.
      return { kind: 'refused', code: 'SYSTEM_INTERNAL_ERROR', message: null };
    }
  }
}

/** One row's treatment: what it looks like, and which catalogue entry says what happened. */
interface Treatment {
  readonly tone: GateTone;
  readonly icon: typeof CircleCheck;
}

const TREATMENTS: Readonly<Record<GateOutcome['kind'], Treatment>> = {
  accepted: { tone: 'accepted', icon: CircleCheck },
  override: { tone: 'anomaly', icon: TriangleAlert },
  blocked_override: { tone: 'anomaly', icon: ShieldAlert },
  blocked: { tone: 'stop', icon: ShieldAlert },
  archived: { tone: 'stop', icon: CircleSlash },
  override_not_permitted: { tone: 'stop', icon: Ban },
  reason_required: { tone: 'anomaly', icon: TriangleAlert },
  duplicate: { tone: 'warning', icon: BadgeCheck },
  revoked: { tone: 'warning', icon: KeyRound },
  superseded: { tone: 'warning', icon: KeyRound },
  blank_unclaimed: { tone: 'warning', icon: CircleHelp },
  blank_void: { tone: 'stop', icon: Ban },
  unknown: { tone: 'stop', icon: CircleHelp },
  malformed: { tone: 'stop', icon: CircleAlert },
  rate_limited: { tone: 'stop', icon: Clock },
  index_version_mismatch: { tone: 'stop', icon: CircleAlert },
  offline: { tone: 'offline', icon: CloudOff },
  refused: { tone: 'stop', icon: CircleAlert },
};

/** The tone of one outcome, which is what decides how loudly it is rendered. */
function toneOf(outcome: GateOutcome): GateTone {
  return TREATMENTS[outcome.kind].tone;
}

/** Whether this outcome is the loud one: a full-screen alert and a sound (AD-31). */
export function isAnomaly(outcome: GateOutcome): boolean {
  return toneOf(outcome) === 'anomaly';
}

const TONE_CLASSES: Readonly<Record<GateTone, string>> = {
  accepted: 'border-success bg-success-surface text-success',
  anomaly: 'border-danger bg-danger-surface text-danger',
  stop: 'border-danger bg-card text-danger',
  warning: 'border-warning bg-warning-surface text-warning',
  offline: 'border-border bg-muted text-muted-foreground',
};

/**
 * The sentence one outcome adds beneath its own row, or `null` when the row says everything.
 *
 * **A refusal this screen has no treatment for still HAS a sentence — its own code's.** Rendering
 * only the generic row is how `PERSON_INSIDE_BUILDING` and `CREDENTIAL_BLANK_ALREADY_CLAIMED` reach
 * a door as "no se ha podido registrar", which tells the operator nothing they can act on. The
 * message is rendered here rather than taken from the wire so that it is in the language THIS
 * operator chose; the server's own rendering is the fallback for the codes whose message
 * interpolates a value this side does not have, and beyond that there is only the row's body,
 * because the one thing worse than a generic sentence at a gate is `{{minutes}}`.
 *
 * It answers the rate-limited hint too, so the two cannot disagree about where a detail comes from.
 */
export function outcomeDetail(
  outcome: GateOutcome,
  locale: Locale,
  messages: { readonly retryAfter: (seconds: number) => string },
): string | null {
  if (outcome.kind === 'rate_limited') {
    return outcome.retryAfterSeconds === null
      ? null
      : messages.retryAfter(outcome.retryAfterSeconds);
  }
  if (outcome.kind !== 'refused') {
    return null;
  }
  const sentence = toErrorBody({ code: outcome.code }, locale).error.message;
  if (!UNFILLED_PLACEHOLDER.test(sentence)) {
    return sentence;
  }
  return outcome.message !== null && !UNFILLED_PLACEHOLDER.test(outcome.message)
    ? outcome.message
    : null;
}

/**
 * The banner one outcome renders as.
 *
 * `role="status"` rather than `alert` for everything but the stops and the anomalies: a gate
 * announces an accepted scan on every person who walks through, and a screen reader that
 * interrupted its user for each one would be turned off within a shift.
 */
export function GateStatusBanner({
  outcome,
  detail,
}: Readonly<{ outcome: GateOutcome; detail?: string }>): ReactNode {
  const { t } = useTranslation();
  const treatment = TREATMENTS[outcome.kind];
  const Icon = treatment.icon;
  const loud = treatment.tone === 'anomaly' || treatment.tone === 'stop';

  return (
    <div
      role={loud ? 'alert' : 'status'}
      data-outcome={outcome.kind}
      data-tone={treatment.tone}
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 text-sm',
        TONE_CLASSES[treatment.tone],
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-5 shrink-0" />
      <div className="grid gap-1">
        <p className="font-semibold">{t(`scan.outcome.${outcome.kind}.title`)}</p>
        <p className="text-foreground/80">{t(`scan.outcome.${outcome.kind}.body`)}</p>
        {detail !== undefined && <p className="text-foreground/80">{detail}</p>}
      </div>
    </div>
  );
}
